import type { ClassroomSection } from '@/types/composition-functions';
import { getClassroomSectionLabel } from '@/utils/classroomLabel';

/**
 * The slice of a timetable's `timeable` union the list query selects:
 * `classroomName` on the Classroom arm, `sectionName` + the parent
 * classroom / section on the ClassroomSection arm.
 */
type Nullable<T> = { [K in keyof T]?: T[K] | null };

export type TimetableTimeableLike = Nullable<Pick<ClassroomSection, 'id'>> & {
  classroomName?: string | null;
  sectionName?: string | null;
  classroom?: { name?: string | null } | null;
  section?: { name?: string | null } | null;
};

export interface TimeableLabelOptions {
  /** The timetable's own title, used only when it is not an auto-generated model name. */
  title?: string | null;
  schoolType?: string | null;
  /** Shown when nothing better is known. Defaults to "Unnamed timetable". */
  fallback?: string;
}

/** Titles the FE generates from the model type, e.g. "Timetable for ClassroomSection (Auto)". */
const AUTO_TITLE = /^Timetable for (Classroom|ClassroomSection)\b/i;

export function isAutoTimetableTitle(title: string | null | undefined): boolean {
  return Boolean(title) && AUTO_TITLE.test(String(title).trim());
}

/**
 * The class a timetable belongs to, as users name it ("JSS 2 Gold"), built
 * through the shared classroom label rules so tertiary schools get "100 Level".
 */
export function getTimeableLabel(
  timeable: TimetableTimeableLike | null | undefined,
  options: TimeableLabelOptions = {},
): string {
  if (timeable) {
    if (timeable.classroomName) return timeable.classroomName;

    const sectionLabel = getClassroomSectionLabel(
      {
        name: timeable.sectionName ?? null,
        classroom: timeable.classroom ?? undefined,
        section: timeable.section ?? null,
      } as ClassroomSection,
      undefined,
      options.schoolType,
    );
    if (sectionLabel) return sectionLabel;
  }

  const title = options.title?.trim();
  if (title && !isAutoTimetableTitle(title)) return title;

  return options.fallback ?? 'Unnamed timetable';
}

/**
 * The title a new timetable is created with: the class as users name it, so the
 * list never has to fall back to the model-name "(Auto)" form.
 */
export function defaultTimetableTitle(className: string | null | undefined, type: string): string {
  const name = className?.trim();
  return name ? `Timetable for ${name}` : `Timetable for ${type} (Auto)`;
}
